/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import authorization from 'live/services/authorization'
import globalConfirmation from 'live/services/global-confirmation'
import i18n from 'live/services/i18n'

import { IReduxActions, IReduxReducers } from '../../../../../../../../../store'
import { IBarrier, IBarrierTypeIcon, IConsequence, IThreat } from '../../../../../../../../../types'
import { Icon } from '../../../../../../../../../utils'
import {
    barrierWidth,
    barrierHeight,
    escalationHeight,
    escalationWidth,
    bowtieTheme,
    unfocusOpacity
} from '../../../../../../../../../utils/constants'
import { EscalationFactorEditor } from './components'
import { BarrierContainer, TextContainer } from './styles'

interface IProps {
    x: number
    y: number
    escId: string
    type: IType
    barrierData: IBarrier
    barrierBrothersY: number
    rootXIndex: number
    isPrimary?: boolean
    onEditClick?: (barrier: IBarrier, type: IType, escId: string) => void
    onDeleteClick?: (e: IBarrier) => void
    selectedBarrier?: IBarrier
    unfocus?: boolean
    closeAll?: string
    selectedThreatConsequence: IThreat | IConsequence
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
}

type IType = 'threat' | 'consequence'

export const BarrierEditor: React.FC<IProps> = ({
    x,
    y,
    escId,
    type,
    barrierData,
    barrierBrothersY = 0,
    rootXIndex,
    isPrimary = false,
    onEditClick,
    onDeleteClick,
    selectedBarrier,
    unfocus,
    closeAll,
    selectedThreatConsequence,
    barrierTypeIconMap
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const { consequencesExpandedBarriers, threatsExpandedBarriers } = useSelector(
        (state: IReduxReducers) => state.bowtieExpandedBarriers
    )
    const [stateExpanded, setStateExpanded] = useState(false)
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    useEffect(() => {
        if (stateExpanded && escId.startsWith(closeAll)) setStateExpanded(false)
    }, [closeAll])

    const toggleEscalation = (): void => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        if (barrierData.escalations.filter(el => el).length) {
            if (expandedBarriers?.length > 0) {
                if (stateExpanded) {
                    dispatch<IReduxActions['bowtieExpandedBarriers']>({
                        type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                        payload: {
                            newExpandedBarriers: expandedBarriers.filter(
                                el => !el.barrierId.startsWith(escId)
                            ) /* might need | after */
                        }
                    })
                    setStateExpanded(false)
                } else {
                    const index = barrierData.escalations.length || 0
                    const newExpanded = []
                    for (let i = 0; i < index; i++) {
                        newExpanded.push(
                            expandedBarriers
                                .filter(el => el.barrierId.startsWith(`${escId}|esc-${barrierData.escalations[i].id}`))
                                .map(d => (d.escalations ?? []).length)
                                .reduce((a, b) => a + b, 0)
                        )
                    }
                    const brothersLength: number = barrierBrothersY + newExpanded.reduce((a, b) => a + b, 0)
                    dispatch<IReduxActions['bowtieExpandedBarriers']>({
                        type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                        payload: {
                            newExpandedBarriers: [
                                ...expandedBarriers,
                                {
                                    barrierId: escId,
                                    rootXIndex: rootXIndex,
                                    escalations:
                                        barrierData.escalations?.map((e, i) => ({
                                            escId: `${escId}|esc-${e.id}`,
                                            x: x + (type === 'threat' ? -escalationWidth : escalationWidth),
                                            y:
                                                y +
                                                escalationHeight +
                                                barrierHeight +
                                                (brothersLength + i) * (barrierHeight * 2 + 30)
                                        })) || []
                                }
                            ]
                        }
                    })
                    setStateExpanded(true)
                }
            }
        }
    }
    const handleDelete = (): void => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: expandedBarriers.filter(el => !el.barrierId.startsWith(escId))
            }
        })
        onDeleteClick(barrierData)
    }

    const handleConfirmDelete = (): void => {
        globalConfirmation.warn(
            i18n('barrier-removal-message', {
                type: i18n('barrier'),

                name: barrierData.name
            }),
            handleDelete,
            { btnText: i18n('Remove') }
        )
    }

    return (
        <>
            {stateExpanded &&
                barrierData.escalations?.map((escalation, index, arr) => {
                    const expanded = []
                    const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
                    for (let i = 0; i < index; i++) {
                        expanded.push(
                            expandedBarriers
                                .filter(el => el.barrierId.startsWith(`${escId}|esc-${arr[i].id}`))
                                .map(d => (d.escalations ?? []).length)
                                .reduce((a, b) => a + b, 0)
                        )
                    }
                    const brothersLength = barrierBrothersY + expanded.reduce((a, b) => a + b, 0)
                    const lineY = y + barrierHeight * 2 + (brothersLength + index) * (barrierHeight * 2 + 30)

                    return (
                        <EscalationFactorEditor
                            key={`${escId}|esc-${escalation.id}`}
                            escId={`${escId}|esc-${escalation.id}`}
                            escalationData={escalation}
                            type={type}
                            x={type === 'threat' ? x - 150 : x + 150}
                            y={lineY}
                            xFather={type === 'consequence' ? x - barrierWidth : x}
                            yFather={y}
                            rootXIndex={rootXIndex}
                            unfocus={!!selectedBarrier || unfocus}
                            barrierTypeIconMap={barrierTypeIconMap}
                        />
                    )
                })}
            <BarrierContainer
                id={escId}
                transform={`translate(${type === 'consequence' ? x - barrierWidth : x}, ${y})`}
                cursor={isPrimary ? 'pointer' : 'default'}
                fill={theme['gray-0']}
                opacity={
                    unfocus || selectedThreatConsequence || (selectedBarrier && selectedBarrier !== barrierData)
                        ? unfocusOpacity
                        : 1
                }
            >
                {bowtieAdmin && isPrimary && (
                    <g className="delete-barrier-button">
                        <rect width={barrierWidth + 20} height={barrierHeight / 2} fill={theme['gray-200']} />
                        <Icon
                            type="delete"
                            scale="1.2"
                            color={theme['gray-200']}
                            auxliarColor={theme['red-500']}
                            x={barrierWidth + 2}
                            clickEvent={handleConfirmDelete}
                        />
                    </g>
                )}
                <rect x={barrierWidth / 2 - 3} y={-15} width={6} height={25} fill={theme['gray-600']} />
                <rect
                    width={barrierWidth}
                    height={barrierHeight}
                    stroke={bowtieTheme.barrierColors.active}
                    strokeWidth={1}
                    // fill="#fff"
                />
                <rect width={barrierWidth} height={14} fill={bowtieTheme.barrierColors.active} />

                <text
                    x={4}
                    y={2}
                    textAnchor="start"
                    dominantBaseline="hanging"
                    fontSize={10}
                    style={{ fontFamily: theme['font-bold'], fill: theme.static['gray-0'] }}
                >
                    Active
                </text>
                <Icon
                    x={
                        barrierData?.criticality?.level !== 'HIGH' && !barrierData?.manualInteraction
                            ? barrierWidth - 14
                            : barrierData?.criticality?.level === 'HIGH' && barrierData?.manualInteraction
                            ? barrierWidth - 45
                            : barrierWidth - 30
                    }
                    y={0}
                    type={barrierData.barrierType?.iconReference}
                    scale="0.8"
                    color={theme.static['gray-0']}
                    barrierTypeIcon={barrierTypeIconMap.get(barrierData.barrierType?.barrierTypeIcon?.id)}
                />
                {barrierData.criticality?.level === 'HIGH' && (
                    <Icon
                        x={barrierData?.manualInteraction ? barrierWidth - 30 : barrierWidth - 14}
                        y={0}
                        type="critical"
                        color={theme.static['gray-0']}
                        scale="0.8"
                    />
                )}
                {barrierData?.manualInteraction && (
                    <Icon x={barrierWidth - 14} type="manual" color={theme.static['gray-0']} scale="0.8" />
                )}
                <foreignObject
                    x={4}
                    y={14 + 4}
                    width={barrierWidth - 8}
                    height={barrierHeight - 14 - 8}
                    style={{ overflow: 'hidden' }}
                >
                    <TextContainer>{barrierData?.name ?? i18n('Barrier')}</TextContainer>
                </foreignObject>
                {/* this is the box of Icon that expand to modal */}

                <rect
                    x={-1}
                    y={-1}
                    width={barrierWidth + 2}
                    height={barrierHeight + 2}
                    stroke="#37A3EF"
                    strokeWidth={4}
                    fill="none"
                    opacity={selectedBarrier === barrierData ? 1 : 0}
                />

                <rect
                    width={barrierWidth}
                    height={barrierHeight}
                    opacity={0}
                    cursor="pointer"
                    onClick={() => onEditClick(barrierData, type, escId)}
                />
                {barrierData?.escalations?.length > 0 && (
                    <Icon
                        type="arrow"
                        x={barrierWidth / 2 - 8}
                        y={barrierHeight + (stateExpanded ? 20 : 4)}
                        scale={stateExpanded ? '1 -1' : '1'}
                        color={bowtieTheme.barrierColors.active}
                        auxliarColor={theme['gray-200']}
                        clickEvent={toggleEscalation}
                    />
                )}
            </BarrierContainer>
        </>
    )
}
